import { useState, type FormEvent } from "react";

type Props = {
  defaultInterest?: string;
  variant?: "light" | "dark";
};

const SERVICES = [
  "Textile Recycling",
  "Confidential Waste Disposal",
  "IT & Media Recycling",
  "Secure Product Destruction",
  "One-off Shredding",
];

export function EnquiryForm({ defaultInterest, variant = "light" }: Props) {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const get = (k: string) => String(data.get(k) || "").trim();
    setStatus("sending");
    try {
      const res = await fetch("/api/public/enquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: get("firstName"),
          lastName: get("lastName"),
          company: get("company"),
          email: get("email"),
          phone: get("phone"),
          interest: get("interest") || defaultInterest || "",
          message: get("message"),
          agreementSigner: get("agreementSigner"),
          website: get("website"),
          sourcePage: window.location.href,
        }),
      });
      const json = (await res.json().catch(() => null)) as { ok?: boolean } | null;
      if (res.ok && json?.ok && !get("website")) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "generate_lead", service: get("interest") || defaultInterest || "unspecified" });
      }
      setStatus(res.ok && json?.ok ? "sent" : "error");
    } catch {
      setStatus("error");
    }
  }
  const sent = status === "sent";

  const wrap =
    variant === "dark"
      ? "rounded-md bg-charcoal/40 p-8 ring-1 ring-cream/15 backdrop-blur"
      : "rounded-md bg-sage-light/30 p-8 ring-1 ring-charcoal/10";
  const label =
    variant === "dark"
      ? "text-[10px] font-semibold uppercase tracking-[0.2em] text-cream/70"
      : "text-[10px] font-semibold uppercase tracking-[0.2em] text-charcoal/70";
  const input =
    variant === "dark"
      ? "w-full border-b border-cream/30 bg-transparent py-2 text-sm text-cream placeholder:text-cream/40 outline-none transition-colors focus:border-cream"
      : "w-full border-b border-charcoal/20 bg-transparent py-2 text-sm outline-none transition-colors focus:border-moss";
  const btn =
    variant === "dark"
      ? "mt-4 w-full rounded-sm bg-cream py-4 text-sm font-medium uppercase tracking-wide text-charcoal transition-colors hover:bg-cream/90"
      : "mt-4 w-full rounded-sm bg-charcoal py-4 text-sm font-medium uppercase tracking-wide text-cream transition-colors hover:bg-charcoal/90";

  if (sent) {
    return (
      <div className={wrap} role="status">
        <p
          className={
            variant === "dark" ? "font-serif text-2xl text-cream" : "font-serif text-2xl text-charcoal"
          }
        >
          Thanks, we'll be in touch shortly.
        </p>
      </div>
    );
  }

  return (
    <form className={`${wrap} relative`} onSubmit={handleSubmit}>
      <div className="grid gap-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Field label="First name" name="firstName" labelClass={label} inputClass={input} required />
          <Field label="Last name" name="lastName" labelClass={label} inputClass={input} required />
        </div>
        <Field label="Business name" name="company" labelClass={label} inputClass={input} required />
        <Field label="Work email" name="email" type="email" labelClass={label} inputClass={input} required />
        <Field label="Phone number" name="phone" type="tel" labelClass={label} inputClass={input} />
        <div className="space-y-2">
          <label htmlFor="interest" className={label}>
            Service interest
          </label>
          <select
            id="interest"
            name="interest"
            defaultValue={defaultInterest}
            className={input}
          >
            {SERVICES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
        <Field
          label="Who signs service agreements at your organisation? (optional)"
          name="agreementSigner"
          placeholder="Role or name"
          labelClass={label}
          inputClass={input}
        />
        <div className="space-y-2">
          <label htmlFor="message" className={label}>
            Message
          </label>
          <textarea id="message" name="message" rows={3} className={input} />
        </div>
        <div
          aria-hidden="true"
          style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}
        >
          <label htmlFor="website">Website</label>
          <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
        </div>
        <button
          type="submit"
          className={`${btn} disabled:cursor-not-allowed disabled:opacity-70`}
          disabled={status === "sending"}
        >
          {status === "sending" ? "Sending…" : "Submit enquiry"}
        </button>
        <p
          className={
            variant === "dark"
              ? "text-center text-xs text-cream/70"
              : "text-center text-xs text-charcoal/70"
          }
        >
          Prefer email?{" "}
          <a className="underline" href="mailto:info@preserveearth.co.uk">
            info@preserveearth.co.uk
          </a>
        </p>
        {status === "error" && (
          <p
            role="alert"
            className={variant === "dark" ? "text-sm text-cream" : "text-sm text-charcoal"}
          >
            Sorry, something went wrong sending your enquiry. Please email us directly at{" "}
            <a className="underline" href="mailto:info@preserveearth.co.uk">info@preserveearth.co.uk</a>
          </p>
        )}
      </div>
    </form>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  placeholder,
  labelClass,
  inputClass,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  labelClass: string;
  inputClass: string;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={name} className={labelClass}>
        {label}
        {required && " *"}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        className={inputClass}
      />
    </div>
  );
}
